import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, Loader, Save, KeyRound, CheckCircle, AlertCircle } from 'lucide-react';
import { HouseManual } from '../types';
import { ownerAuth, ownerGetManual, ownerSaveManual, ownerUploadVideo, ownerUploadImage } from '../services/apiService';
import ManualEditorForm from '../components/ManualEditorForm';

const EMPTY_MANUAL: HouseManual = {
    houseName: '', address: '', badge: '', welcomeMessage: '', assistancePhone: '',
    checkinVideoUrl: '', intercomCode: '', keyBoxCode: '', wifiName: '', wifiPassword: '',
    items: []
};

const OwnerManualEdit = () => {
    const { editSlug } = useParams<{ editSlug: string }>();
    const [password, setPassword] = useState('');
    const [authed, setAuthed] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    const [manual, setManual] = useState<HouseManual>(EMPTY_MANUAL);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    useEffect(() => {
        if (feedback) {
            const t = setTimeout(() => setFeedback(null), 3000);
            return () => clearTimeout(t);
        }
    }, [feedback]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editSlug || !password) return;
        setAuthLoading(true);
        setAuthError(null);
        try {
            const res = await ownerAuth(editSlug, password);
            if (res.success) {
                setAuthed(true);
                await loadManual();
            } else {
                setAuthError(res.message || 'Invalid credentials.');
            }
        } catch (err: any) {
            setAuthError(err?.response?.data?.message || 'Invalid credentials.');
        } finally {
            setAuthLoading(false);
        }
    };

    const loadManual = async () => {
        if (!editSlug) return;
        setLoading(true);
        try {
            const data = await ownerGetManual(editSlug, password);
            if (data) {
                setManual({
                    houseName: data.houseName || '',
                    address: data.address || '',
                    badge: data.badge || '',
                    welcomeMessage: data.welcomeMessage || '',
                    assistancePhone: data.assistancePhone || '',
                    checkinVideoUrl: data.checkinVideoUrl || '',
                    intercomCode: data.intercomCode || '',
                    keyBoxCode: data.keyBoxCode || '',
                    wifiName: data.wifiName || '',
                    wifiPassword: data.wifiPassword || '',
                    items: (data.items || []).map((it: any) => ({
                        id: it.id, title: it.title || '', instructions: it.instructions || '',
                        videoUrl: it.videoUrl || '', content: it.content || '', displayOrder: it.displayOrder
                    }))
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        if (!editSlug) return;
        setSaving(true);
        try {
            const res = await ownerSaveManual(editSlug, password, manual);
            if (res.success) {
                setFeedback({ type: 'success', message: 'Manual saved.' });
                if (res.data) {
                    setManual(prev => ({
                        ...prev,
                        items: (res.data.items || []).map((it: any) => ({
                            id: it.id, title: it.title || '', instructions: it.instructions || '',
                            videoUrl: it.videoUrl || '', content: it.content || '', displayOrder: it.displayOrder
                        }))
                    }));
                }
            } else {
                setFeedback({ type: 'error', message: res.message || 'Save failed.' });
            }
        } catch {
            setFeedback({ type: 'error', message: 'Server error.' });
        } finally {
            setSaving(false);
        }
    };

    const uploadVideo = async (blob: Blob, onProgress: (pct: number) => void): Promise<string> => {
        const res = await ownerUploadVideo(editSlug!, password, blob, onProgress);
        if (res.success) return res.data as string;
        throw new Error(res.message || 'Upload failed');
    };

    const uploadImage = async (file: File): Promise<string> => {
        const res = await ownerUploadImage(editSlug!, password, file);
        if (res.success) return res.data as string;
        throw new Error(res.message || 'Upload failed');
    };

    // ============================================================
    // LOGIN VIEW
    // ============================================================
    if (!authed) {
        return (
            <>
                <Helmet>
                    <meta name="robots" content="noindex, nofollow"/>
                    <title>House Manual — Owner Access</title>
                </Helmet>
                <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 font-sans">
                    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 w-full max-w-md p-10">
                        <div className="flex flex-col items-center mb-8">
                            <div className="w-16 h-16 rounded-2xl bg-brand/10 flex items-center justify-center mb-4">
                                <Lock className="w-7 h-7 text-brand"/>
                            </div>
                            <h1 className="font-serif font-bold text-2xl text-primary">Owner Access</h1>
                            <p className="text-sm text-gray-500 mt-2 text-center">Enter the password you received to edit your house manual.</p>
                        </div>

                        <form onSubmit={handleLogin} className="space-y-5">
                            <div>
                                <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Owner Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    autoFocus
                                    className="w-full p-4 bg-gray-50 border-transparent rounded-xl focus:bg-white focus:ring-2 focus:ring-brand/20 outline-none transition-all font-mono"
                                    placeholder="••••••••"
                                />
                            </div>

                            {authError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 shrink-0"/> {authError}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={authLoading || !password}
                                className="w-full bg-brand text-white py-4 rounded-xl font-bold hover:bg-[#c42e40] transition-all shadow-md flex items-center justify-center gap-3 disabled:opacity-60"
                            >
                                {authLoading ? <Loader className="w-5 h-5 animate-spin"/> : <KeyRound className="w-5 h-5"/>}
                                Unlock
                            </button>
                        </form>
                    </div>
                </div>
            </>
        );
    }

    // ============================================================
    // FORM VIEW
    // ============================================================
    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex, nofollow"/>
                <title>{manual.houseName ? `Edit — ${manual.houseName}` : 'Edit House Manual'}</title>
            </Helmet>

            <div className="min-h-screen bg-gray-50 font-sans pb-32">
                <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
                    <div className="max-w-3xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
                        <div>
                            <h1 className="font-serif font-bold text-lg md:text-xl text-primary">House Manual Editor</h1>
                            <p className="text-xs text-gray-500">{manual.houseName || 'Untitled'}</p>
                        </div>
                        <button
                            onClick={save}
                            disabled={saving}
                            className="bg-brand text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-[#c42e40] transition-all shadow-md flex items-center gap-2 disabled:opacity-60"
                        >
                            {saving ? <Loader className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                            Save
                        </button>
                    </div>
                </header>

                {feedback && (
                    <div className="max-w-3xl mx-auto px-4 md:px-6 mt-4">
                        <div className={`p-4 rounded-xl flex items-center gap-3 text-sm font-bold ${feedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                            {feedback.type === 'success' ? <CheckCircle className="w-5 h-5"/> : <AlertCircle className="w-5 h-5"/>}
                            {feedback.message}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center justify-center py-20 text-gray-400">
                        <Loader className="w-8 h-8 animate-spin"/>
                    </div>
                ) : (
                    <main className="max-w-3xl mx-auto px-4 md:px-6 py-8">
                        <ManualEditorForm
                            manual={manual}
                            setManual={setManual}
                            onSave={save}
                            saving={saving}
                            uploadVideo={uploadVideo}
                            uploadImage={uploadImage}
                            onError={(msg) => setFeedback({ type: 'error', message: msg })}
                        />
                    </main>
                )}
            </div>
        </>
    );
};

export default OwnerManualEdit;
